import { useState, useEffect } from "react";
import { Cookie, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const STORAGE_KEY = "juke-cookie-consent";
// Legacy key from earlier iterations — migrated once on load.
const LEGACY_STORAGE_KEY = "juke_cookie_consent";

// Post-purchase / transactional routes and time-critical driver flows
// where the banner would obscure a primary CTA — skip it entirely.
const SUPPRESS_ROUTES = [
  "/rate/",
  "/receipt/",
  "/b2b/sign-contract/",  // e-signing — never obscure the consent/sign controls
  "/supplier/sign-contract/",  // supplier e-signing
  "/supplier/request/",   // supplier accept/decline action page
  "/experiences/pay/",    // deposit payment page — never obscure pay button
  "/driver/offer/",    // 30-second countdown — never obscure
  "/admin",            // authenticated admin console — never obscure
  "/driver",           // authenticated driver PWA — never obscure
];

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const { pathname } = useLocation();
  const suppressed = SUPPRESS_ROUTES.some((p) => pathname.startsWith(p));

  useEffect(() => {
    if (suppressed) return;
    // Migrate legacy key once so returning visitors don't get re-prompted.
    try {
      const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
      if (legacy && !localStorage.getItem(STORAGE_KEY)) {
        localStorage.setItem(STORAGE_KEY, legacy);
      }
      if (legacy) localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch { /* private mode — ignore */ }

    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      // Delay slightly so it doesn't fight with hero animations
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, [suppressed]);

  const record = (choice) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ choice, at: new Date().toISOString() })
      );
    } catch {
      // Storage may be blocked (private mode) — banner will re-appear next visit, which is acceptable.
    }
    setVisible(false);
  };

  if (!visible || suppressed) return null;

  return (
    <div
      data-testid="cookie-consent-banner"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-[70] px-4 pb-4 sm:px-6 sm:pb-6 pointer-events-none"
    >
      <div className="pointer-events-auto max-w-4xl mx-auto rounded-2xl border border-white/10 bg-black/85 backdrop-blur-xl shadow-[0_12px_48px_rgba(0,0,0,0.6)] p-5 sm:p-6">
        <div className="flex items-start gap-4">
          <div className="hidden sm:flex w-10 h-10 shrink-0 rounded-full bg-[#C5A059]/15 border border-[#C5A059]/30 items-center justify-center">
            <Cookie className="w-5 h-5 text-[#C5A059]" />
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059] mb-1">
              Cookies & Privacy
            </p>
            <p className="text-sm text-zinc-300 leading-relaxed">
              We use essential cookies to run this site and optional analytics cookies to
              understand how visitors use it. You can accept all cookies or reject non-essential
              ones. Read our{" "}
              <Link
                to="/privacy"
                data-testid="cookie-privacy-link"
                className="text-[#C5A059] hover:text-[#D4AF37] underline underline-offset-2"
              >
                Privacy Policy
              </Link>{" "}
              for details.
            </p>

            <div className="mt-4 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 sm:items-center">
              <button
                type="button"
                data-testid="cookie-reject-btn"
                onClick={() => record("reject")}
                className="h-10 px-5 rounded-full text-xs uppercase tracking-[0.2em] text-zinc-300 border border-white/15 hover:border-white/30 hover:text-white transition-colors"
              >
                Reject non-essential
              </button>
              <button
                type="button"
                data-testid="cookie-accept-btn"
                onClick={() => record("accept")}
                className="h-10 px-5 rounded-full text-xs uppercase tracking-[0.2em] bg-[#C5A059] hover:bg-[#D4AF37] text-black font-semibold transition-colors"
              >
                Accept all
              </button>
            </div>
          </div>

          <button
            type="button"
            aria-label="Dismiss"
            data-testid="cookie-close-btn"
            onClick={() => record("dismiss")}
            className="text-zinc-500 hover:text-white transition-colors -mt-1 -mr-1 p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
